import { useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import type { CustomGroupSearch, CustomGroupSearchRow } from '@metron/shared';
import {
  meterSearchSchema,
  parseCustomGroupSearchParams,
  SEARCH_DEFAULT_PAGE_SIZE,
  toCustomGroupSearchParams,
  toMeterSearchParams,
} from '@metron/shared';
import { DataTable } from '../components/data-table/data-table.tsx';
import { DataTableColumnHeader } from '../components/data-table/data-table-column-header.tsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import { CustomGroupSearchFilters } from '../components/custom-group-search/CustomGroupSearchFilters.tsx';
import { useTableUrlState } from '../hooks/useTableUrlState.ts';
import { useCustomGroupFilter } from '../hooks/useCustomGroupFilter.ts';
import { useCustomers } from '../hooks/useCustomers.ts';

/** Meter Search scoped to a group's own sensor group (and its customer). */
export function groupMetersUrl(group: CustomGroupSearchRow): string {
  const spec = meterSearchSchema.parse({
    customerId: group.customerId,
    sensorGroupId: group.sensorGroupId,
  });
  return `/search/meters?${toMeterSearchParams(spec)}`;
}

const na = <span className='text-muted-foreground'>N/A</span>;

// Column ids match the spec's sortBy values, so useTableUrlState's `sortBy`
// param is directly the spec's. Address isn't a server sort key.
const columns: ColumnDef<CustomGroupSearchRow>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Group' />,
    cell: ({ row }) => <div className='font-medium'>{row.original.name}</div>,
  },
  {
    accessorKey: 'address',
    enableSorting: false,
    header: ({ column }) => <DataTableColumnHeader column={column} title='Address' />,
    cell: ({ row }) => row.original.address || na,
  },
  {
    accessorKey: 'customerName',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Customer' />,
    cell: ({ row }) => row.original.customerName ?? na,
  },
  {
    accessorKey: 'meterCount',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Meters' />,
    cell: ({ row }) => <div className='text-right'>{row.original.meterCount.toLocaleString()}</div>,
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Created' />,
    cell: ({ row }) => new Date(row.original.createdAt).toLocaleDateString(),
  },
];

/**
 * Custom Group Search (Smart Search results, MET-3529)
 *
 * The custom group counterpart of the other search pages: the whole search
 * lives in the URL, filters render as editable chips, and paging/sorting use
 * the shared URL-backed table state. A row opens Meter Search scoped to that
 * group, so its member meters are one click away.
 */
export function CustomGroupSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { customers, isLoading: customersLoading } = useCustomers();
  const { pagination, sorting, onPaginationChange, onSortingChange } = useTableUrlState({
    defaultPageSize: SEARCH_DEFAULT_PAGE_SIZE,
  });

  const parsed = useMemo(() => parseCustomGroupSearchParams(searchParams), [searchParams]);
  const spec = parsed.success ? parsed.data : null;
  const { data, isLoading, isFetching, error } = useCustomGroupFilter(spec);

  const setSpec = (next: CustomGroupSearch) => setSearchParams(toCustomGroupSearchParams(next));

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl font-bold tracking-tight'>Custom Group Search</h1>
        <p className='text-muted-foreground'>
          Filter custom groups by customer, name, and member count. Open a group to see its meters.
        </p>
      </div>

      {!parsed.success
        ? (
          <Card>
            <CardHeader>
              <CardTitle>This search link isn't valid</CardTitle>
              <CardDescription>
                {parsed.error.issues.map((i) => `${i.path.join('.') || 'search'}: ${i.message}`)
                  .join('; ')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link className='underline' to='/search/custom-groups'>Start a new search</Link>
            </CardContent>
          </Card>
        )
        : (
          <Card>
            <CardHeader className='space-y-4'>
              <CustomGroupSearchFilters
                spec={parsed.data}
                customers={customers}
                customersLoading={customersLoading}
                onChange={setSpec}
              />
              <CardDescription aria-live='polite'>
                {isLoading
                  ? 'Searching…'
                  : error
                  ? null
                  : `${(data?.total ?? 0).toLocaleString()} group${data?.total === 1 ? '' : 's'}${
                    isFetching ? ' · updating…' : ''
                  }`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error
                ? (
                  <p role='alert' className='text-sm text-destructive py-4 text-center'>
                    Search failed: {error instanceof Error ? error.message : 'unknown error'}
                  </p>
                )
                : (
                  <DataTable
                    columns={columns}
                    data={data?.customGroups ?? []}
                    onRowClick={(group) => navigate(groupMetersUrl(group))}
                    manualPagination
                    rowCount={data?.total ?? 0}
                    pagination={pagination}
                    onPaginationChange={onPaginationChange}
                    manualSorting
                    sorting={sorting}
                    onSortingChange={onSortingChange}
                    emptyMessage={isLoading
                      ? 'Searching…'
                      : 'No custom groups match these filters.'}
                  />
                )}
            </CardContent>
          </Card>
        )}
    </div>
  );
}
